import { useCallback, useRef, useState } from 'react';
import { ApiError, uploadVideoFile } from '../api/client.ts';
import { probeVideoFile } from './videoProbe.ts';

/** idle → probing → uploading → uploaded (the bytes are on the server) */
export type TransferPhase = 'idle' | 'probing' | 'uploading' | 'uploaded';

export interface PendingUploadState {
  file: File | null;
  durationSec: number;
  /** Set once the transfer finishes — this is what the save call refers to. */
  uploadId: string | null;
  progress: number;
  phase: TransferPhase;
  error: string | null;
  busy: boolean;
  pick: (chosen: File | undefined) => Promise<void>;
}

/**
 * Phase 1 of an upload, shared by the new-video and edit screens: probe the
 * file in the browser, then start sending it immediately. Transferring on pick
 * rather than on submit means a multi-GB upload over a phone connection runs
 * while the title is being typed instead of after it.
 */
export function usePendingUpload(): PendingUploadState {
  const abortRef = useRef<(() => void) | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [durationSec, setDurationSec] = useState(0);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<TransferPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  const pick = useCallback(async (chosen: File | undefined) => {
    if (!chosen) return;

    // Picking again while a transfer runs abandons it rather than racing it.
    abortRef.current?.();
    setError(null);
    setFile(chosen);
    setUploadId(null);
    setProgress(0);
    setPhase('probing');

    // Duration and poster frame are read in the browser — no ffmpeg server-side.
    const probe = await probeVideoFile(chosen);
    setDurationSec(probe.durationSec);

    setPhase('uploading');
    const { promise, abort } = uploadVideoFile(chosen, probe.thumb, setProgress);
    abortRef.current = abort;

    try {
      const pending = await promise;
      setUploadId(pending.uploadId);
      setPhase('uploaded');
    } catch (err) {
      setPhase('idle');
      setFile(null);
      setProgress(0);
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить видео');
    } finally {
      abortRef.current = null;
    }
  }, []);

  return {
    file,
    durationSec,
    uploadId,
    progress,
    phase,
    error,
    busy: phase === 'probing' || phase === 'uploading',
    pick,
  };
}
